import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext, useMemo } from 'react';
import { Box, Typography, Button, Link, NativeSelect, styled, Grid, Stack, Tooltip } from '@mui/material';
import { Link as RouteLink, useNavigate, useParams } from 'react-router';
import HomeIcon from '@mui/icons-material/Home';
import GitHubIcon from '@mui/icons-material/GitHub';
import BugReportIcon from '@mui/icons-material/BugReport';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MainContext } from '../../context';
import { addQuery, createRoute, formatFileSize, getTargetPlatformDisplayName, getEngineDisplayName } from '../../utils';
import { DelayedLoadIndicator } from '../../components/delayed-load-indicator';
import { SanitizedMarkdown } from '../../components/sanitized-markdown';
import { Timestamp } from '../../components/timestamp';
import { VERSION_ALIASES } from '../../extension-registry-types';
import { ExtensionListRoutes } from '../extension-list/extension-list-routes';
import { ExtensionDetailRoutes } from './extension-detail-routes';
import { ExtensionDetailDownloadsMenu } from './extension-detail-downloads-menu';
import { WeeklyDownloads } from './weekly-downloads';
import { useExtensionReadme } from './use-extension-details';
const NO_README = '## No README available';
export const ExtensionDetailOverview = props => {
    const { pageSettings } = useContext(MainContext);
    const params = useParams();
    const navigate = useNavigate();
    const { data: readmeContent, isLoading: readmeLoading } = useExtensionReadme(props.extension);
    const readme = props.extension.files.readme ? readmeContent : NO_README;
    const worksWithEngines = useMemo(() => {
        const engines = props.extension.engines;
        if (engines == null) {
            return null;
        }
        const data = Object.keys(engines)
            .map(engine => ({
            key: engine,
            name: getEngineDisplayName(engine),
            version: engines[engine]
        }))
            .filter(d => d.name); // only display engines with a known display name
        return (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, xs: 'auto', children: _jsx(Stack, { spacing: 0.5, children: data.map(d => (_jsxs(Box, { component: 'span', sx: { color: 'primary.dark', fontWeight: 'fontWeightBold' }, children: [d.name, ":"] }, d.key))) }) }), _jsx(Grid, { item: true, xs: true, children: _jsx(Stack, { spacing: 0.5, children: data.map(d => (_jsx(Box, { component: 'span', children: d.version }, d.key))) }) })] }));
    }, [props.extension.engines]);
    const worksWithTargetPlatforms = useMemo(() => {
        return (_jsxs(Grid, { item: true, xs: 12, children: [_jsx(Box, { component: 'span', sx: { color: 'primary.dark', fontWeight: 'fontWeightBold' }, children: "Target Platforms:" }), Object.keys(props.extension.downloadSizes ?? {}).length > 0 ? (_jsx(Tooltip, { title: 'Download sizes; the installed size is larger.', children: _jsx(Box, { component: 'span', tabIndex: 0, sx: { ml: 0.5, color: 'text.secondary', cursor: 'help' }, children: _jsx(InfoOutlinedIcon, { fontSize: 'inherit', sx: { verticalAlign: 'top' } }) }) })) : null, _jsx(Stack, { component: 'ul', spacing: 0.5, sx: { m: 0, mt: 0.5, p: 0, listStyle: 'none' }, children: Object.keys(props.extension.downloads).map(targetPlatform => {
                        const displayName = getTargetPlatformDisplayName(targetPlatform);
                        const size = props.extension.downloadSizes?.[targetPlatform];
                        return displayName ? (_jsxs(Box, { component: 'li', sx: { display: 'flex', flexWrap: 'wrap' }, children: [_jsx(Box, { component: 'span', sx: { minWidth: '12rem', pr: 2 }, children: displayName }), size === undefined ? null : _jsx("span", { children: formatFileSize(size) })] }, targetPlatform)) : null;
                    }) })] }));
    }, [props.extension.downloads, props.extension.downloadSizes]);
    const renderVersionSection = () => {
        const { extension } = props;
        const allVersions = Object.keys(extension.allVersions).filter(version => VERSION_ALIASES.indexOf(version) < 0);
        return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'h6', children: "Version" }), allVersions.length === 1 ? (_jsx(Typography, { variant: 'body1', display: 'inline', children: allVersions[0] })) : (_jsx(NativeSelect, { name: 'Version', value: extension.version, onChange: event => props.selectVersion(event.target.value), inputProps: { 'aria-label': 'Version' }, children: allVersions.map(version => (_jsx("option", { children: version }, version))) })), extension.preRelease ? (_jsx(Box, { component: 'span', sx: { color: 'primary.dark', fontStyle: 'italic', ml: 2, p: '4px' }, children: "(pre-release version)" })) : (''), extension.timestamp ? (_jsxs(Box, { mt: 1, mb: 1, children: ["Published ", _jsx(Timestamp, { value: extension.timestamp })] })) : null] }));
    };
    const renderIdentifierSection = () => {
        return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'h6', children: "Unique Identifier" }), _jsx(Typography, { variant: 'body2', children: _jsx("code", { children: `${extension.namespace}.${extension.name}` }) })] }));
    };
    const renderAliasesSection = (otherAliases, sx) => {
        const { extension } = props;
        const aliasButtons = otherAliases.length
            ? otherAliases.map(alias => {
                const arr = [ExtensionDetailRoutes.ROOT, extension.namespace, extension.name];
                if (params.target) {
                    arr.push(params.target);
                }
                if (alias !== 'latest') {
                    arr.push(alias);
                }
                const route = createRoute(arr);
                return (_jsx(Button, { sx: sx, size: 'small', variant: 'outlined', title: `Switch to version with "${alias}" alias`, onClick: () => navigate(route), children: alias }, alias));
            })
            : '';
        return (_jsxs(_Fragment, { children: [_jsxs(Typography, { variant: 'h6', children: ["Version Alias", extension.versionAlias.length > 1 ? 'es' : ''] }), extension.versionAlias.map((alias, idx) => (_jsxs(Box, { component: 'span', sx: { color: 'primary.dark', fontWeight: 'fontWeightBold' }, children: [idx > 0 ? ', ' : '', alias] }, alias))), aliasButtons ? (_jsxs(_Fragment, { children: [extension.versionAlias.length > 0 ? ' ' : '', "Switch to ", aliasButtons] })) : ('')] }));
    };
    const renderButtonList = (kind, title, arr, sx) => {
        const filtered = Array.from(new Set(arr)).sort((a, b) => a.localeCompare(b));
        return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'h6', children: title }), filtered.map((buttonLabel) => (_jsx(Button, { sx: sx, size: 'small', variant: 'outlined', title: kind === 'category'
                        ? `Search for extensions in "${buttonLabel}" category`
                        : `Search for extensions containing "${buttonLabel}"`, onClick: () => {
                        const route = addQuery(ExtensionListRoutes.MAIN, [{ key: kind, value: buttonLabel }]);
                        navigate(route);
                    }, children: buttonLabel }, buttonLabel)))] }));
    };
    const renderResourceLink = (label, resourceLink, href) => {
        if (!href || !(href.startsWith('http') || href.startsWith('mailto'))) {
            return '';
        }
        let icon;
        if (label === 'Homepage') {
            icon = _jsx(HomeIcon, { fontSize: 'small' });
        }
        else if (label === 'Repository' && href.startsWith('https://github.com/')) {
            icon = _jsx(GitHubIcon, { fontSize: 'small' });
        }
        else if (label === 'Bugs') {
            icon = _jsx(BugReportIcon, { fontSize: 'small' });
        }
        else if (label === "Q'n'A") {
            icon = _jsx(QuestionAnswerIcon, { fontSize: 'small' });
        }
        return (_jsx(Box, { children: _jsxs(Link, { href: href, target: '_blank', variant: 'body2', color: 'secondary', underline: 'hover', sx: resourceLink, children: [icon, "\u00A0", label] }) }));
    };
    const StyledRouteLink = styled(RouteLink)(({ theme }) => ({
        textDecoration: 'none',
        color: theme.palette.text.primary,
        '&:hover': {
            textDecoration: 'underline'
        }
    }));
    const renderExtensionRef = (ref) => {
        return (_jsx(Box, { children: _jsxs(StyledRouteLink, { to: createRoute([ExtensionDetailRoutes.ROOT, ref.namespace, ref.extension]), children: [ref.namespace, ".", ref.extension] }) }, `${ref.namespace}.${ref.extension}`));
    };
    if (!readme) {
        return _jsx(DelayedLoadIndicator, { loading: readmeLoading });
    }
    const { extension } = props;
    const tagButton = {
        fontWeight: 'normal',
        textTransform: 'none',
        mr: 0.5,
        mb: 0.5,
        padding: '1px 6px'
    };
    const resourceLink = {
        display: 'flex',
        alignItems: 'center',
        mt: 0.5
    };
    const resourcesGroup = {
        display: 'flex',
        flexDirection: 'column'
    };
    const ClaimNamespace = pageSettings.elements.claimNamespace;
    const ReportAbuse = pageSettings.elements.reportAbuse;
    const DownloadTerms = pageSettings.elements.downloadTerms;
    const otherAliases = Object.keys(extension.allVersions).filter(version => extension.versionAlias.indexOf(version) < 0 && VERSION_ALIASES.indexOf(version) >= 0);
    // filter internal tags
    const tags = extension.tags?.filter(t => !t.startsWith('__'));
    let downloadButton = null;
    if (extension.downloadable && extension.downloads && Object.keys(extension.downloads).length > 1) {
        downloadButton = _jsx(ExtensionDetailDownloadsMenu, { downloads: extension.downloads });
    }
    else if (extension.downloadable && extension.downloads && Object.keys(extension.downloads).length == 1) {
        downloadButton = (_jsx(Button, { variant: 'contained', color: 'secondary', sx: { mt: 2 }, href: extension.downloads[Object.keys(extension.downloads)[0]], children: "Download" }));
    }
    return (_jsxs(Box, { sx: {
            display: 'flex',
            gap: 4,
            flexDirection: {
                xs: 'column-reverse',
                md: 'row'
            }
        }, children: [_jsx(Box, { flex: 5, sx: { minWidth: 0, overflow: 'auto' }, children: _jsx(SanitizedMarkdown, { content: readme }) }), _jsxs(Box, { sx: {
                    flex: 1,
                    display: 'flex',
                    width: '100%',
                    minWidth: { xs: 0, md: '290px' },
                    gap: 2,
                    flexDirection: 'column'
                }, children: [_jsx(WeeklyDownloads, { extension: extension }), _jsxs(Box, { sx: resourcesGroup, children: [_jsx(Box, { children: renderVersionSection() }), otherAliases.length || extension.versionAlias.length ? (_jsx(Box, { children: renderAliasesSection(otherAliases, tagButton) })) : ('')] }), _jsx(Box, { sx: resourcesGroup, children: _jsx(Box, { children: renderIdentifierSection() }) }), _jsxs(Box, { sx: resourcesGroup, children: [extension.categories && extension.categories.length > 0 ? (_jsx(Box, { children: renderButtonList('category', 'Categories', extension.categories, tagButton) })) : null, tags && tags.length > 0 ? (_jsx(Box, { mt: extension.categories && extension.categories.length > 0 ? 2 : 0, children: renderButtonList('search', 'Tags', tags, tagButton) })) : null] }), extension.downloads ? (_jsx(Box, { sx: resourcesGroup, children: _jsxs(Box, { children: [_jsx(Typography, { variant: 'h6', children: "Works With" }), _jsxs(Grid, { container: true, spacing: 0.5, children: [worksWithEngines, worksWithTargetPlatforms] })] }) })) : null, _jsxs(Box, { sx: resourcesGroup, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: 'h6', children: "Resources" }), renderResourceLink('Homepage', resourceLink, extension.homepage), renderResourceLink('Repository', resourceLink, extension.repository), renderResourceLink('Bugs', resourceLink, extension.bugs), renderResourceLink("Q'n'A", resourceLink, extension.qna), downloadButton, DownloadTerms &&
                                        extension.downloadable &&
                                        extension.downloads &&
                                        Object.keys(extension.downloads).length > 0 ? (_jsx(DownloadTerms, {})) : null] }), extension.bundledExtensions !== undefined && extension.bundledExtensions.length > 0 ? (_jsxs(Box, { mt: 2, children: [_jsx(Typography, { variant: 'h6', children: "Bundled Extensions" }), extension.bundledExtensions.map(ref => renderExtensionRef(ref))] })) : null, extension.dependencies !== undefined && extension.dependencies.length > 0 ? (_jsxs(Box, { mt: 2, children: [_jsx(Typography, { variant: 'h6', children: "Dependencies" }), extension.dependencies.map(ref => renderExtensionRef(ref))] })) : null, _jsxs(Box, { mt: 2, children: [ClaimNamespace ? (_jsx(ClaimNamespace, { namespace: extension.namespace, extension: extension, sx: resourceLink })) : (''), ReportAbuse ? _jsx(ReportAbuse, { extension: extension, sx: resourceLink }) : ''] })] })] })] }));
};
//# sourceMappingURL=extension-detail-overview.js.map